import { useSelector } from 'react-redux';
import {
  ToggleOn as ToggleOnIcon,
  ToggleOff as ToggleOffIcon,
  Public as PublicIcon,
  PublicOff as PublicOffIcon,
} from '@mui/icons-material';
import { hasPermission } from '../../utils/permissions';

/**
 * True if the current user may perform `permission` (e.g. 'products.update').
 * The codebase carries permissions in two shapes (a flat `user.permissions`
 * string array from /auth/me, and `rolePermissions`/`directPermissions`
 * objects the sidebar uses) — accept either, SUPER_ADMIN always passes.
 */
export const useCanUpdate = (permission) => {
  const user = useSelector((state) => state.auth.user);
  return hasPermission(user, permission) || !!user?.permissions?.includes(permission);
};

/**
 * `sx` for a plain MUI <TableRow> in the few list pages that aren't AG Grid
 * (suppliers, customers, promotions): gray + dimmed when isActive === false.
 * Theme-aware so it also works in dark mode.
 */
export const inactiveRowSx = (row) =>
  row?.isActive === false
    ? { opacity: 0.5, bgcolor: 'action.disabledBackground', '& td': { color: 'text.disabled' } }
    : {};

/**
 * Builds the GridActionsCell action for the row's active toggle — icon-only,
 * per the project's actions-column convention. `onToggle(row)` does the work.
 */
export const buildToggleAction = ({ row, onToggle, t, canToggle = true }) => {
  const inactive = row.isActive === false;
  return {
    key: 'toggle-active',
    icon: inactive ? ToggleOffIcon : ToggleOnIcon,
    title: inactive ? t('common.activate') : t('common.deactivate'),
    color: inactive ? 'default' : 'success',
    onClick: () => onToggle(row),
    hidden: !canToggle,
  };
};

/**
 * GridActionsCell action for the row's "sell on the website" switch (sellOnline). Icon-only; the
 * item stays sellable from the admin either way. `onToggle(row)` does the work.
 */
export const buildSellOnlineAction = ({ row, onToggle, t, canToggle = true }) => {
  const offline = row.sellOnline === false;
  return {
    key: 'toggle-sell-online',
    icon: offline ? PublicOffIcon : PublicIcon,
    title: offline ? t('sellOnline.enable') : t('sellOnline.disable'),
    color: offline ? 'default' : 'success',
    onClick: () => onToggle(row),
    hidden: !canToggle,
  };
};
